import { SprintAPI } from '../../utils/api';
import { showToast } from '../../components/Toast';
import { notifyInboxTargetsGone, notifyInboxTargetsRestored } from '../../utils/notificationInbox';
import {
  resolveParentTaskId,
  persistCascadeTrash,
  dispatchNestedSprintTrashed,
  removeLinkingMissionFromParent,
} from './deleteHelpers';
import {
  isTemplateItem,
  computeExpiresAt,
  findNestedChildren,
  snapshotByIds,
  revertFromSnapshots,
  logAudit,
} from './utils';

/**
 * Soft-delete a sprint (move to trash).
 *
 * Instead of permanently deleting, we add a `deletedAt` timestamp.
 * The sprint can be restored later or permanently deleted from trash.
 * Items are auto-deleted by DynamoDB TTL after 30 days (via `expiresAt`).
 *
 * TREE-WIDE TRASH: The whole subtree is soft-deleted together. Descendants get
 * the same deletedAt/expiresAt plus `_trashedViaAncestorId` pointing at the
 * explicitly-trashed root, so:
 *   - the bin lists only the root (descendants are implied),
 *   - restoring the root cascade-restores exactly the descendants trashed with it,
 *   - DynamoDB TTL purges the whole tree at once instead of only the root
 *     (previously children survived the parent's TTL and leaked out as orphans).
 * parentSprintId stays intact on descendants so the tree restores unchanged.
 *
 * IMPORTANT: Must fetch full sprint data before updating to avoid overwriting tasks!
 *
 * options.preserveLinkedMission: when true (e.g. clicking the X on a nested-campaign
 * pill inside a mission card), the linking mission task in the parent is KEPT and only
 * unlinked (nestedSprintId cleared) — the caller owns the parent-side unlink/persist.
 */
export async function deleteSprint({
  sprintId,
  options = {},
  sprints,
  setSprints,
  selectedSprintId,
  selectedTemplateId,
  setSelectedSprintId,
  setSelectedTemplateId,
  setCurrentView,
  VIEWS,
}) {
  const { preserveLinkedMission = false } = options;
  const sprintMetadata = sprints.find((s) => s.id === sprintId);
  if (!sprintMetadata) return;

  const deletedAt = new Date().toISOString();
  const expiresAt = computeExpiresAt();

  // Descendants trashed together with this sprint. Ones already in the trash
  // (individually deleted earlier) keep their own deletedAt/TTL untouched.
  const cascadeDescendants = findNestedChildren(sprints, sprintId).filter((c) => !c.deletedAt);
  const cascadeIds = new Set(cascadeDescendants.map((c) => c.id));

  logAudit('SPRINT_TRASHED', {
    sprintId,
    sprintName: sprintMetadata?.name,
    parentSprintId: sprintMetadata?.parentSprintId,
    parentTaskId: sprintMetadata?.parentTaskId,
    expiresAt,
    cascadedChildren: cascadeDescendants.map((c) => c.id),
  }, sprintId);

  // ── OPTIMISTIC: update UI immediately, before any network round-trips ──
  // Mark the local (metadata) copies as trashed so the sidebar reacts instantly.
  // The bookkeeping fields are set from what we know locally; the background
  // persist below rebuilds them from the full server copy.
  const localSnapshots = snapshotByIds(sprints, [sprintId, ...cascadeIds]);
  const goneCampaignIds = [sprintId, ...cascadeDescendants.map((c) => c.id)];
  notifyInboxTargetsGone({ campaignIds: goneCampaignIds });
  setSprints((prev) => prev.map((s) => {
    if (s.id === sprintId) {
      return {
        ...s,
        deletedAt,
        expiresAt,
        _originalParentSprintId: s.parentSprintId,
        _originalParentTaskId: s.parentTaskId,
      };
    }
    if (cascadeIds.has(s.id)) {
      return { ...s, deletedAt, expiresAt, _trashedViaAncestorId: sprintId };
    }
    return s;
  }));

  // If deleted sprint (or a descendant of it) was selected, clear selection
  if (selectedSprintId === sprintId || cascadeIds.has(selectedSprintId)) {
    setSelectedSprintId(null);
    setCurrentView(VIEWS.EMPTY);
  }
  if ((selectedTemplateId === sprintId && isTemplateItem(sprintMetadata)) || cascadeIds.has(selectedTemplateId)) {
    setSelectedTemplateId(null);
    setCurrentView(VIEWS.EMPTY);
  }

  const revertOptimisticDelete = () => {
    revertFromSnapshots(setSprints, localSnapshots);
    notifyInboxTargetsRestored({ campaignIds: goneCampaignIds });
  };

  // ── BACKGROUND: fetch full data and persist ──
  // CRITICAL: Must persist from full sprint data, not metadata, to avoid wiping tasks!
  let fullSprint;
  try {
    fullSprint = await SprintAPI.get(sprintId);
    if (!fullSprint) {
      throw new Error('Sprint not found');
    }
  } catch (error) {
    console.error('[TRASH] Error fetching sprint:', error);
    revertOptimisticDelete();
    showToast(`Failed to move "${sprintMetadata.name || 'campaign'}" to trash — please try again`, { type: 'error' });
    return;
  }

  const parentTaskId = await resolveParentTaskId(fullSprint, sprintId);
  const trashedSprint = {
    ...fullSprint,
    deletedAt,
    expiresAt,
    _originalParentSprintId: fullSprint.parentSprintId,
    _originalParentTaskId: parentTaskId,
  };

  setSprints((prev) => prev.map((s) => (s.id === sprintId ? trashedSprint : s)));

  try {
    await SprintAPI.update(sprintId, trashedSprint);
    console.log('✅ Sprint moved to trash:', fullSprint?.name);
  } catch (error) {
    console.error('Failed to trash sprint:', error);
    revertOptimisticDelete();
    showToast(`Failed to move "${fullSprint.name || 'campaign'}" to trash — please try again`, { type: 'error' });
    return;
  }

  await persistCascadeTrash({ cascadeDescendants, sprintId, deletedAt, expiresAt, setSprints });

  if (preserveLinkedMission) {
    dispatchNestedSprintTrashed(fullSprint.parentSprintId, parentTaskId, sprintId);
    return;
  }

  await removeLinkingMissionFromParent(fullSprint.parentSprintId, sprintId, setSprints);
}
